import React, {
  CSSProperties, FocusEvent, KeyboardEvent, useCallback, useEffect, useId, useRef, useState,
} from 'react';
import { Dropdown, DropdownMenuProps } from 'react-bootstrap';

import Checkbox from 'components/DesignSystem/Checkbox';
import Icon from 'components/DesignSystem/Icon';
import FilterField from 'components/Flux/Personalized/MyStudies/FilterBar/FilterField';
import {
  FLUX_STUDY_STATUS,
  FLUX_STUDY_STATUS_CODES,
  FluxStudyStatusCode,
} from 'constants/fluxStudyStatus';

export interface StatusFilterProps {
  /** Status marcados (`filters.status`); vazio = todos. */
  value: readonly FluxStudyStatusCode[];
  /** Recebe os status marcados, sempre na ordem de `FLUX_STUDY_STATUS_CODES`. */
  onChange: (status: FluxStudyStatusCode[]) => void;
}

/**
 * Opções do menu, na ordem do contrato. A bolinha usa a cor da D25 (`constants/fluxStudyStatus.ts`)
 * pela variável CSS `--my-studies-status-dot`: o Checkbox do DS só aceita rótulo em texto.
 */
const STATUS_OPTIONS = FLUX_STUDY_STATUS_CODES.map((code) => ({
  code,
  label: FLUX_STUDY_STATUS[code].label,
  style: { '--my-studies-status-dot': FLUX_STUDY_STATUS[code].dot } as CSSProperties,
}));

/** O menu abre a 6 px do botão, como no protótipo (.dc.html:144, `top: calc(100% + 6px)`). */
const MENU_POPPER_CONFIG: DropdownMenuProps['popperConfig'] = {
  modifiers: [{ name: 'offset', options: { offset: [0, 6] } }],
};

/** Índice que `focusOption` limita ao último status. */
const LAST_OPTION = Number.MAX_SAFE_INTEGER;

/** Teclas que movem o foco entre os status dentro do menu. */
const NAVIGATION_KEYS = ['ArrowDown', 'ArrowUp', 'Home', 'End'] as const;
type NavigationKey = typeof NAVIGATION_KEYS[number];

const isNavigationKey = (key: string): key is NavigationKey => (
  (NAVIGATION_KEYS as readonly string[]).includes(key)
);

/**
 * Texto do botão (.dc.html:713): nenhum marcado = "Todos os status"; um = o rótulo dele;
 * dois ou mais = "N status".
 *
 * @param value - Status marcados.
 */
function toggleLabel(value: readonly FluxStudyStatusCode[]): string {
  if (value.length === 0) return 'Todos os status';
  if (value.length === 1) return FLUX_STUDY_STATUS[value[0]].label;
  return `${value.length} status`;
}

/**
 * Filtro de status da barra de Meus Estudos (#1995): botão com o resumo da seleção e um menu de
 * multisseleção com o Checkbox do DS e a bolinha de cor de cada status (.dc.html:140-153).
 *
 * Controlado por props: quem guarda os status é o `useMyStudiesFilters` da tela.
 *
 * Teclado: Enter, Espaço ou seta para baixo abrem o menu e levam o foco ao 1º status; seta para
 * cima abre no último (padrão APG de menu button). As setas, Home e End navegam; Espaço marca; Esc
 * fecha e devolve o foco ao botão; Tab para fora fecha.
 *
 * Marcar não fecha o menu, para dar para escolher vários de uma vez. Quem garante isso são as
 * próprias opções: são checkboxes, não `Dropdown.Item`, então não disparam a seleção que fecha o
 * Dropdown, e o clique dentro do menu não conta como clique fora. O `autoClose="outside"` fica
 * como guarda, caso alguém troque as opções por `Dropdown.Item`.
 */
export default function StatusFilter({ value, onChange }: StatusFilterProps) {
  const labelId = useId();
  const toggleId = useId();
  const [open, setOpen] = useState(false);
  // Onde o foco cai ao abrir: o 1º status, ou o último quando a abertura veio da seta para cima
  const focusOnOpen = useRef(0);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const checkboxes = useCallback(() => Array.from(
    menuRef.current?.querySelectorAll<HTMLInputElement>('input[type="checkbox"]') ?? [],
  ), []);

  const focusOption = useCallback((index: number) => {
    const boxes = checkboxes();
    boxes[Math.max(0, Math.min(index, boxes.length - 1))]?.focus({ preventScroll: true });
  }, [checkboxes]);

  // Abrir leva o foco a um status, como num menu; ao fechar com o foco dentro, o Dropdown do
  // react-bootstrap devolve o foco ao botão
  useEffect(() => {
    if (!open) return;
    focusOption(focusOnOpen.current);
    focusOnOpen.current = 0;
  }, [open, focusOption]);

  const handleToggle = useCallback((nextShow: boolean) => setOpen(nextShow), []);

  // Setas no botão. O Dropdown só procura `.dropdown-item` (aqui as opções são checkboxes) e não
  // abre com a seta para cima. Fechado, a seta para baixo fica com o Dropdown, que abre o menu.
  const handleToggleKeyDown = useCallback((event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (open) {
        focusOption(LAST_OPTION);
      } else {
        focusOnOpen.current = LAST_OPTION;
        setOpen(true);
      }
      return;
    }
    if (open && event.key === 'ArrowDown') {
      event.preventDefault();
      focusOption(0);
    }
  }, [open, focusOption]);

  // Tab para fora do menu fecha: o Dropdown ignora o Tab que sai de um input. Só fecha quando o
  // foco vai para outro elemento: sem `relatedTarget` (clique numa área sem foco), quem fecha é o
  // clique fora; fechar aqui faria o clique no próprio botão reabrir o menu (o Safari não foca
  // botões no clique)
  const handleMenuBlur = useCallback((event: FocusEvent<HTMLDivElement>) => {
    const next = event.relatedTarget;
    if (!(next instanceof Node)) return;
    if (event.currentTarget.contains(next) || toggleRef.current?.contains(next)) return;
    setOpen(false);
  }, []);

  // O Dropdown ignora as setas quando o foco está num input; a navegação entre os status é daqui
  const handleMenuKeyDown = useCallback((event: KeyboardEvent<HTMLDivElement>) => {
    if (!isNavigationKey(event.key)) return;
    event.preventDefault();
    const boxes = checkboxes();
    const current = boxes.indexOf(event.target as HTMLInputElement);
    const target: Record<NavigationKey, number> = {
      ArrowDown: current + 1,
      ArrowUp: current - 1,
      Home: 0,
      End: boxes.length - 1,
    };
    focusOption(target[event.key]);
  }, [checkboxes, focusOption]);

  const toggleStatus = useCallback((code: FluxStudyStatusCode) => {
    const marked = value.includes(code)
      ? value.filter((item) => item !== code)
      : [...value, code];
    onChange(FLUX_STUDY_STATUS_CODES.filter((item) => marked.includes(item)));
  }, [value, onChange]);

  return (
    <FilterField label="STATUS" labelId={labelId} className="my-studies-status-filter">
      <Dropdown show={open} onToggle={handleToggle} autoClose="outside">
        <Dropdown.Toggle
          ref={toggleRef}
          as="button"
          type="button"
          id={toggleId}
          className="my-studies-status-toggle"
          // Nome "STATUS <seleção>": o rótulo do campo e o resumo do próprio botão
          aria-labelledby={`${labelId} ${toggleId}`}
          onKeyDown={handleToggleKeyDown}
        >
          <span className="my-studies-status-toggle-label">{toggleLabel(value)}</span>
          <span className="my-studies-status-toggle-chevron" aria-hidden="true">
            <Icon iconType="down_arrow" color="currentColor" size="small" />
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu
          ref={menuRef}
          popperConfig={MENU_POPPER_CONFIG}
          className="my-studies-status-menu"
          onKeyDown={handleMenuKeyDown}
          onBlur={handleMenuBlur}
        >
          <div role="group" aria-labelledby={labelId} className="my-studies-status-options">
            {STATUS_OPTIONS.map(({ code, label, style }) => (
              <div key={code} className="my-studies-status-option" style={style}>
                <Checkbox
                  label={label}
                  checked={value.includes(code)}
                  onChange={() => toggleStatus(code)}
                />
              </div>
            ))}
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </FilterField>
  );
}
